// Procedural cartoon avatars. Each persona has a fixed look (skin, hair,
// accessory); the face reacts to live state — the mouth follows mood, the
// eyelids droop with fatigue, sweat appears under stress, and sleeping
// personas close their eyes.

type Look = { skin: string; hair: string; hairStyle: 'short' | 'long' | 'bun' | 'bald' | 'curly' | 'cap'; accent: string; glasses?: boolean; beard?: boolean };

const LOOKS: Record<string, Look> = {
  mari: { skin: '#d9a173', hair: '#4a3328', hairStyle: 'bun', accent: '#ef6f5e', glasses: true },
  dana: { skin: '#7a4e32', hair: '#1f1612', hairStyle: 'curly', accent: '#4aa3df' },
  wes: { skin: '#f0c9a4', hair: '#c98b3a', hairStyle: 'cap', accent: '#f7b538' },
  priya: { skin: '#b07650', hair: '#17110f', hairStyle: 'long', accent: '#4caf6e' },
  gus: { skin: '#f2d3b8', hair: '#d8d8d8', hairStyle: 'bald', accent: '#e8590c', beard: true },
  bev: { skin: '#f1cfb2', hair: '#a8543a', hairStyle: 'short', accent: '#8b6fd6', glasses: true },
  kai: { skin: '#e6b98e', hair: '#22232b', hairStyle: 'short', accent: '#3a7bd5', glasses: true },
  rosa: { skin: '#c68b5e', hair: '#2a1a14', hairStyle: 'long', accent: '#f08c00' },
};

const FALLBACK_SKINS = ['#f1cfb2', '#d9a173', '#b07650', '#7a4e32'];
const FALLBACK_HAIR = ['#2a1a14', '#4a3328', '#c98b3a', '#888'];

function lookFor(key: string): Look {
  const known = LOOKS[key];
  if (known) return known;
  let h = 0;
  for (const c of key) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return { skin: FALLBACK_SKINS[h % 4]!, hair: FALLBACK_HAIR[(h >> 3) % 4]!, hairStyle: 'short', accent: '#4aa3df' };
}

export function Avatar({
  personaKey,
  mood = 0,
  fatigue = 0,
  stress = 0,
  asleep = false,
  size = 48,
  ring,
}: {
  personaKey: string;
  mood?: number;
  fatigue?: number;
  stress?: number;
  asleep?: boolean;
  size?: number;
  /** Ring colour (e.g. "live" or activity). */
  ring?: string;
}) {
  const l = lookFor(personaKey);
  const m = Math.max(-1, Math.min(1, mood));
  // Mouth: a quadratic curve whose control point dips for a smile and rises for a frown.
  const mouthY = 41;
  const curve = mouthY + m * 6;
  const lid = asleep ? 1 : Math.min(0.75, Math.max(0, fatigue - 0.25));
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" role="img" aria-label={`${personaKey} avatar`} className="avatar">
      <circle cx="32" cy="32" r="31" fill={l.accent} opacity="0.25" />
      {ring ? <circle cx="32" cy="32" r="30" fill="none" stroke={ring} strokeWidth="3" /> : null}
      {/* shoulders */}
      <path d="M12 64c2-11 10-16 20-16s18 5 20 16Z" fill={l.accent} />
      {/* hair behind */}
      {l.hairStyle === 'long' ? <path d="M15 30c0-12 8-19 17-19s17 7 17 19v20H15Z" fill={l.hair} /> : null}
      {/* head */}
      <ellipse cx="32" cy="32" rx="14" ry="16" fill={l.skin} />
      {/* hair on top */}
      {l.hairStyle === 'short' ? <path d="M18 28c0-9 6-14 14-14s14 5 14 14c-4-5-9-6-14-6s-10 1-14 6Z" fill={l.hair} /> : null}
      {l.hairStyle === 'long' ? <path d="M18 29c1-9 7-14 14-14s13 5 14 14c-5-6-10-7-14-7s-9 1-14 7Z" fill={l.hair} /> : null}
      {l.hairStyle === 'bun' ? (
        <>
          <circle cx="32" cy="12" r="6" fill={l.hair} />
          <path d="M18 28c0-9 6-13 14-13s14 4 14 13c-4-4-9-5-14-5s-10 1-14 5Z" fill={l.hair} />
        </>
      ) : null}
      {l.hairStyle === 'curly' ? (
        <g fill={l.hair}>
          {[18, 23, 28, 33, 38, 43, 46].map((x, i) => (
            <circle key={x} cx={x} cy={i % 2 ? 17 : 20} r="5" />
          ))}
        </g>
      ) : null}
      {l.hairStyle === 'cap' ? (
        <>
          <path d="M17 26c0-8 6-13 15-13s15 5 15 13Z" fill="#2f6fb5" />
          <path d="M40 24h13c0 3-3 4-13 4Z" fill="#2f6fb5" />
        </>
      ) : null}
      {l.hairStyle === 'bald' ? <path d="M18 27c0-3 1-5 2-6m24 6c0-3-1-5-2-6" stroke={l.hair} strokeWidth="3" fill="none" /> : null}
      {/* eyes */}
      <g>
        <ellipse cx="26" cy="31" rx="2.4" ry={2.6 * (1 - lid) + 0.3} fill="#26222b" />
        <ellipse cx="38" cy="31" rx="2.4" ry={2.6 * (1 - lid) + 0.3} fill="#26222b" />
      </g>
      {l.glasses ? (
        <g fill="none" stroke="#26222b" strokeWidth="1.6">
          <circle cx="26" cy="31" r="5" />
          <circle cx="38" cy="31" r="5" />
          <path d="M31 31h2" />
        </g>
      ) : null}
      {/* cheeks */}
      <circle cx="21" cy="37" r="2.6" fill="#ef6f5e" opacity={0.25 + Math.max(0, m) * 0.3} />
      <circle cx="43" cy="37" r="2.6" fill="#ef6f5e" opacity={0.25 + Math.max(0, m) * 0.3} />
      {l.beard ? <path d="M20 36c2 9 7 12 12 12s10-3 12-12c-3 4-7 5-12 5s-9-1-12-5Z" fill={l.hair} /> : null}
      {/* mouth */}
      {asleep ? (
        <circle cx="32" cy={mouthY} r="1.8" fill="#7a3b35" />
      ) : (
        <path d={`M26 ${mouthY} Q32 ${curve} 38 ${mouthY}`} stroke="#7a3b35" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      )}
      {/* stress sweat */}
      {stress > 0.6 && !asleep ? <path d="M46 22c2 3 2 5 0 6-2-1-2-3 0-6Z" fill="#7cc4f2" /> : null}
      {asleep ? (
        <text x="45" y="16" fontSize="11" fontWeight="800" fill="#8b6fd6" fontFamily="Fredoka, Nunito, sans-serif">
          z
        </text>
      ) : null}
    </svg>
  );
}
